import React from 'react';
import { Search, X, QrCode, User, ArrowLeft } from 'lucide-react';
import { useSession } from '../lib/session';
import { requireAuth } from './AuthGate';

// The brand's darkest teal, already used on the badge header in ClaimGate.
const BAR_BG = '#0F4C4A';

/**
 * AppTopBar — the single bar across the top of every screen.
 *
 * Replaces the old AppHeader. The logo, the desktop nav links and the bell are
 * all gone: navigation now lives entirely in the bottom bar, which buys back
 * the vertical space that header was spending on a wordmark.
 *
 * Home passes `onSearchChange` and gets the search field. Every other screen
 * passes `title` and gets its name in the same slot. The right-hand pair —
 * the QR button, then the member's own avatar — is constant, so those two
 * controls never move between screens.
 *
 * The QR button took the slot the Ihsan Points pill used to hold. Scanning is
 * something a member does standing in front of somebody, on any screen, so it
 * earns a fixed position; a points balance is something they look up, so it
 * moved into Profile where the badge already lived.
 */
export default function AppTopBar({
  title,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search for services, classes...',
  onOpenScan,
  onOpenProfile,
  onBack
}) {
  const isSearch = typeof onSearchChange === 'function';
  const session = useSession();

  return (
    <div
      className="sticky top-0 z-30 px-4 pt-3 pb-3"
      style={{ backgroundColor: BAR_BG }}
      data-testid="app-top-bar"
    >
      <div className="desktop-shell flex items-center gap-2.5">
        {onBack && !isSearch && (
          <button
            type="button"
            data-testid="top-bar-back"
            onClick={onBack}
            aria-label="Back"
            className="flex-shrink-0 h-11 w-8 -ml-1.5 flex items-center justify-center text-white"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        {isSearch ? (
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              type="search"
              data-testid="search-input"
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              autoComplete="off"
              enterKeyHint="search"
              className="w-full h-11 rounded-full bg-white pl-10 pr-10 text-sm text-[#0F172A] placeholder:text-muted-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-white/60"
            />
            {searchValue ? (
              <button
                type="button"
                data-testid="search-clear"
                aria-label="Clear search"
                onClick={() => onSearchChange('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        ) : (
          <h1 className="flex-1 min-w-0 truncate text-lg font-bold text-white" data-testid="top-bar-title">
            {title}
          </h1>
        )}

        {/* Scan — reachable from every screen, same hit area as the avatar */}
        <button
          type="button"
          data-testid="top-bar-scan"
          onClick={onOpenScan}
          aria-label="Scan a QR code"
          className="flex-shrink-0 h-11 w-11 rounded-full bg-white shadow-sm flex items-center justify-center hover:bg-white/90 transition-colors"
        >
          <QrCode className="h-5 w-5 text-[#0F4C4A]" strokeWidth={2.2} />
        </button>

        {/* The member — avatar when signed in; a persistent Sign in pill when not,
            so there is always an obvious way into signup/login from any screen. */}
        {session ? (
          <button
            type="button"
            data-testid="top-bar-profile"
            onClick={onOpenProfile}
            aria-label="You"
            className="flex-shrink-0 h-11 w-11 rounded-full bg-white shadow-sm flex items-center justify-center hover:bg-white/90 transition-colors"
          >
            <User className="h-5 w-5 text-[#0F4C4A]" strokeWidth={2.2} />
          </button>
        ) : (
          <button
            type="button"
            data-testid="top-bar-signin"
            onClick={() => requireAuth()}
            aria-label="Sign in or create an account"
            className="flex-shrink-0 h-11 px-4 rounded-full bg-white shadow-sm flex items-center justify-center text-sm font-semibold text-[#0F4C4A] hover:bg-white/90 transition-colors"
          >
            Sign in
          </button>
        )}
      </div>
    </div>
  );
}
